import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  LOCALE_ID,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReturnRequestFilters } from '../../../../../pages/admin-dashboard/return-of-requests-list/return-of-requests-list.model';
import { FilterDateComponent } from '../../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { FilterItemSearch } from '../../../../../shared/ui/shared-filters/filter-item-search/filter-item-search';
import { FilterSortingComponent } from '../../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { PharmacyAutoComplete } from '../../../invoice-list-component/shared/pharmacy-auto-complete/pharmacy-auto-complete';
import { CreatorAutoComplete } from '../../../payment-requests-component/payment-requests-list-component/payment-requests-filter/creator-auto-complete';
import { FilterMultiSelectComponent } from '../../../../../shared/ui/shared-filters/filter-multi-select-component/filter-multi-select-component';

@Component({
  selector: 'app-return-requests-filter',
  standalone: true,
  imports: [
    CommonModule,
    FilterDateComponent,
    FilterSortingComponent,
    FilterItemSearch,
    PharmacyAutoComplete,
    CreatorAutoComplete,
    FilterMultiSelectComponent,
  ],
  templateUrl: './return-requests-filter.html',
  styleUrl: './return-requests-filter.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReturnRequestsFilterComponent {
  private locale = inject(LOCALE_ID);
  isArabic = computed(() => String(this.locale).startsWith('ar'));

  initialFilters = input<ReturnRequestFilters>({
    orderId: '',
    pharmacyId: null,
    statusIds: [],
    createdOnFrom: null,
    createdOnTo: null,
    createdById: null,
    orderBy: 'LAST_MODIFICATION_DATE',
    orderDir: 'DESC',
  });

  statusOptions = input<{ id: string; titleEn: string; titleAr: string }[]>([]);

  applyFilters = output<ReturnRequestFilters>();
  resetFilters = output<void>();

  orderId = linkedSignal(() => this.initialFilters().orderId);
  pharmacyId = linkedSignal<number | null>(() => this.initialFilters().pharmacyId);
  statusIds = linkedSignal<string[]>(() => [...this.initialFilters().statusIds]);
  createdOnFrom = linkedSignal<string | null>(() => this.initialFilters().createdOnFrom);
  createdOnTo = linkedSignal<string | null>(() => this.initialFilters().createdOnTo);
  createdById = linkedSignal<number | null>(() => this.initialFilters().createdById);
  orderBy = linkedSignal(() => this.initialFilters().orderBy);
  orderDir = linkedSignal<'ASC' | 'DESC'>(() => this.initialFilters().orderDir);

  pharmacyClear = signal(0);
  creatorClear = signal(0);
  showStatusDropdown = signal(false);

  sortOptions = signal([
    { value: 'LAST_MODIFICATION_DATE', labelEn: 'Last Modified On', labelAr: 'تاريخ التعديل' },
    { value: 'CREATION_DATE', labelEn: 'Created On', labelAr: 'تاريخ الإنشاء' },
    { value: 'RETURN_VALUE', labelEn: 'Return Value', labelAr: 'قيمة المرتجع' },
    { value: 'STATUS', labelEn: 'Status', labelAr: 'الحالة' },
  ]);

  orderIdInvalid = computed(() => {
    const v = this.orderId();
    return !!v && !/^\d+$/.test(v);
  });

  dateInvalid = computed(() => {
    const from = this.createdOnFrom();
    const to = this.createdOnTo();
    if (!from || !to) return false;
    return new Date(from) > new Date(to);
  });

  futureDateInvalid = computed(() => {
    const from = this.createdOnFrom();
    const to = this.createdOnTo();
    const today = new Date().toISOString().split('T')[0];
    return (!!from && from > today) || (!!to && to > today);
  });

  dateErrorMsg = computed(() => {
    if (this.futureDateInvalid()) {
      return this.isArabic() ? 'لا يمكن اختيار تواريخ في المستقبل.' : 'Future dates are not allowed.';
    }
    if (this.dateInvalid()) {
      return this.isArabic()
        ? 'تاريخ البداية لا يمكن أن يكون بعد تاريخ النهاية.'
        : 'From date cannot be after To date.';
    }
    return '';
  });

  isFormValid = computed(() => !this.orderIdInvalid() && !this.dateInvalid() && !this.futureDateInvalid());

  filters = computed<ReturnRequestFilters>(() => ({
    orderId: this.orderId(),
    pharmacyId: this.pharmacyId(),
    statusIds: this.statusIds(),
    createdOnFrom: this.createdOnFrom(),
    createdOnTo: this.createdOnTo(),
    createdById: this.createdById(),
    orderBy: this.orderBy(),
    orderDir: this.orderDir(),
  }));

  onOrderIdChange(value: string): void {
    this.orderId.set(value);
  }

  onPharmacySelected(pharmacy: { id?: number } | null): void {
    this.pharmacyId.set(pharmacy?.id ?? null);
  }

  onCreatorSelected(creator: { id?: number } | null): void {
    this.createdById.set(creator?.id ?? null);
  }

  toggleStatusDropdown(): void {
    this.showStatusDropdown.update((v) => !v);
  }

  onStatusChange(ids: string[]): void {
    this.statusIds.set(ids);
  }

  toggleSortOrder(): void {
    this.orderDir.update((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
  }

  onApply(): void {
    if (!this.isFormValid()) return;
    this.applyFilters.emit(this.filters());
  }

  onReset(): void {
    this.orderId.set('');
    this.pharmacyId.set(null);
    this.statusIds.set([]);
    this.createdOnFrom.set(null);
    this.createdOnTo.set(null);
    this.createdById.set(null);
    this.orderBy.set('LAST_MODIFICATION_DATE');
    this.orderDir.set('DESC');
    this.pharmacyClear.update((v) => v + 1);
    this.creatorClear.update((v) => v + 1);
    this.resetFilters.emit();
  }
}
